import { useRef, useEffect } from "react";
import {
  CaretUp, CaretDown, Check, ArrowsClockwise, Plus, PencilSimple,
  Trash, Info, Lock,
} from "@phosphor-icons/react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";

export function DataTable({
  columns, data: filteredAndSortedData, allData: data, loading, sorting, sortConfig,
  handleSort, rowLocks, user, loadingAction, isAdmin,
  LEHRGANG_OPTIONS, MANNSCHAFT_OPTIONS,
  handleLehrgangChange, handleMannschaftChange,
  handleEditEntry, handleDeleteEntry, handleRestoreEntry,
  showOverlayThen, setGlobalOverlay, setHistoryModalRow,
  setShowUploadDialog, searchTerm,
}) {
  const scrollRef = useRef(null);
  const savedScrollRef = useRef(0);

  // Scroll to top only when sorting changes
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [sortConfig]);

  // Restore scroll position after data re-renders (not sorting)
  useEffect(() => {
    if (sorting || !scrollRef.current) return;
    scrollRef.current.scrollTop = savedScrollRef.current;
  }, [filteredAndSortedData, sorting]);

  // Save scroll position before any action triggers a data reload
  const saveScroll = () => {
    if (scrollRef.current) savedScrollRef.current = scrollRef.current.scrollTop;
  };

  return (
    <div className="flex-1 min-h-0 bg-card border border-border rounded overflow-hidden flex flex-col">
      <div className="flex-1 overflow-auto" ref={scrollRef}>
        <table className="data-table text-[10px] sm:text-xs w-max">
          <thead className="bg-secondary sticky top-0 z-10">
            <tr>
              {columns.map((col) => {
                const sortable = col.key !== "nr" && col.key !== "actions" && col.key !== "lehrgang" && col.key !== "mannschaft";
                return (
                  <th
                    key={col.key}
                    data-testid={`sort-${col.key}`}
                    onClick={() => sortable && handleSort(col.key)}
                    className={`text-left px-1.5 sm:px-2 py-1.5 sm:py-2 whitespace-nowrap text-[10px] sm:text-xs font-semibold ${sortable ? "cursor-pointer hover:bg-primary/10" : ""} transition-colors select-none`}
                  >
                    <span className="flex items-center gap-0.5 sm:gap-1">
                      {col.label}
                      {sortable && (
                        <span className="text-muted-foreground">
                          {sortConfig.key === col.key ? (
                            sortConfig.direction === "asc" ? <CaretUp className="h-2.5 w-2.5 sm:h-3 sm:w-3" weight="bold" /> : <CaretDown className="h-2.5 w-2.5 sm:h-3 sm:w-3" weight="bold" />
                          ) : (
                            <CaretUp className="h-2.5 w-2.5 sm:h-3 sm:w-3 opacity-30" />
                          )}
                        </span>
                      )}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading || sorting ? (
              <tr>
                <td colSpan={columns.length} className="text-center py-8 sm:py-12">
                  <div className="flex items-center justify-center gap-2 sm:gap-3">
                    <div className="animate-spin h-4 w-4 sm:h-5 sm:w-5 border-2 border-primary border-t-transparent rounded-full" />
                    <span className="text-muted-foreground text-xs sm:text-sm">{sorting ? "Sortiere..." : "Lade Daten..."}</span>
                  </div>
                </td>
              </tr>
            ) : filteredAndSortedData.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="text-center py-8 sm:py-12">
                  <div className="text-muted-foreground text-xs sm:text-sm">
                    {searchTerm ? "Keine Treffer gefunden" : "Keine Daten vorhanden"}
                  </div>
                  {isAdmin && !searchTerm && (
                    <button onClick={() => setShowUploadDialog(true)} className="mt-2 text-xs sm:text-sm text-primary underline">
                      CSV-Dateien hochladen
                    </button>
                  )}
                </td>
              </tr>
            ) : (
              filteredAndSortedData.map((row, idx) => {
                const rowId = row._id;
                const isDeleted = row.deleted;
                const lockInfo = rowLocks[rowId];
                const isLockedByOther = lockInfo && lockInfo.username !== user?.username;
                return (
                  <tr key={rowId} className={`table-row-animate border-t border-border ${isDeleted ? 'opacity-40 bg-destructive/5' : 'table-row-hover'}`}>
                    {columns.map((col) => (
                      <td key={col.key} className={`px-1.5 sm:px-2 py-0.5 sm:py-1 whitespace-nowrap ${isDeleted ? 'line-through' : ''}`}>
                        {col.key === "nr" ? (
                          <span className="text-muted-foreground font-medium no-underline" style={{ textDecoration: 'none' }}>{idx + 1}</span>
                        ) : col.key === "aktiv" ? (
                          row[col.key] === "Ja" ? (
                            <span className="inline-flex items-center gap-0.5 sm:gap-1 text-primary">
                              <Check className="h-2.5 w-2.5 sm:h-3 sm:w-3" weight="bold" />
                              <span className="hidden sm:inline">Ja</span>
                            </span>
                          ) : (
                            <span className="text-muted-foreground">{row[col.key] || "-"}</span>
                          )
                        ) : col.key === "lehrgang" ? (
                          isDeleted || isLockedByOther ? (
                            <span className="text-muted-foreground text-[10px] sm:text-xs">{row.lehrgang || "-"}</span>
                          ) : loadingAction === `lehrgang-${rowId}` ? (
                            <div className="flex items-center gap-1 h-5 sm:h-6"><div className="animate-spin h-3 w-3 border-2 border-primary border-t-transparent rounded-full" /></div>
                          ) : (
                            <Select value={row.lehrgang || "_none"} onValueChange={(v) => { saveScroll(); handleLehrgangChange(rowId, row, v); }}>
                              <SelectTrigger className="h-5 sm:h-6 w-20 sm:w-28 text-[10px] sm:text-xs"><SelectValue placeholder="-" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="_none">-</SelectItem>
                                {LEHRGANG_OPTIONS.map((opt) => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          )
                        ) : col.key === "mannschaft" ? (
                          isDeleted || isLockedByOther ? (
                            <span className="text-muted-foreground text-[10px] sm:text-xs">{row.mannschaft || "-"}</span>
                          ) : loadingAction === `mannschaft-${rowId}` ? (
                            <div className="flex items-center gap-1 h-5 sm:h-6"><div className="animate-spin h-3 w-3 border-2 border-primary border-t-transparent rounded-full" /></div>
                          ) : (
                            <Select value={row.mannschaft || "_none"} onValueChange={(v) => { saveScroll(); handleMannschaftChange(rowId, row, v); }}>
                              <SelectTrigger className="h-5 sm:h-6 w-20 sm:w-24 text-[10px] sm:text-xs"><SelectValue placeholder="-" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="_none">-</SelectItem>
                                {MANNSCHAFT_OPTIONS.map((opt) => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          )
                        ) : col.key === "actions" ? (
                          isDeleted ? (
                            <button
                              data-testid={`restore-entry-${rowId}`}
                              onClick={() => { saveScroll(); handleRestoreEntry(rowId); }}
                              className="p-0.5 sm:p-1 rounded hover:bg-primary/10 text-primary transition-colors"
                              title="Wiederherstellen"
                              disabled={loadingAction === `restore-${rowId}`}
                              style={{ textDecoration: 'none' }}
                            >
                              {loadingAction === `restore-${rowId}` ? (
                                <div className="animate-spin h-3 w-3 border-2 border-primary border-t-transparent rounded-full" />
                              ) : (
                                <ArrowsClockwise className="h-3 w-3" />
                              )}
                            </button>
                          ) : isLockedByOther ? (
                            <div className="flex items-center gap-1 text-amber-500" data-testid={`locked-indicator-${rowId}`} title={`Gesperrt von ${lockInfo.username}`}>
                              <Lock className="h-3 w-3" weight="fill" />
                              <span className="text-[9px] whitespace-nowrap font-medium">{lockInfo.username}</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-0">
                              <button data-testid={`info-entry-${rowId}`} onClick={() => { saveScroll(); showOverlayThen(() => setHistoryModalRow(rowId)); }} className="p-0.5 sm:p-1 rounded hover:bg-blue-500/10 text-muted-foreground hover:text-blue-500 transition-colors" title="Änderungshistorie">
                                <Info className="h-3 w-3" />
                              </button>
                              <button
                                data-testid={`edit-entry-${rowId}`}
                                onClick={async () => { saveScroll(); setGlobalOverlay(true); try { await handleEditEntry(rowId, row); } finally { setGlobalOverlay(false); } }}
                                className="p-0.5 sm:p-1 rounded hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors"
                                title="Bearbeiten"
                              >
                                <PencilSimple className="h-3 w-3" />
                              </button>
                              <button data-testid={`delete-entry-${rowId}`} onClick={() => { saveScroll(); showOverlayThen(() => handleDeleteEntry(rowId, row)); }} className="p-0.5 sm:p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors" title="Löschen">
                                <Trash className="h-3 w-3" />
                              </button>
                            </div>
                          )
                        ) : (
                          row[col.key] || <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
